import { useState } from 'react';
import { useSearchParams } from 'react-router';
import type { HorarioSemanal, Maquina, Material, Parametro, TipoLaser } from '@laser/core';
import {
  useEliminarMaquina, useEliminarMaterial, useEliminarParametro, useGuardarMaquina, useGuardarMaterial, useGuardarParametro,
  useMaquinas, useMateriales, useParametros,
} from '../api/hooks';
import { Campo, Cargando, Confirmar, Dialogo, EntradaNumero, EntradaPesos, ErrorCaja, Insignia, mensajeError, useAvisar } from '../componentes/ui';
import { medidas, numero, pesos, porcentaje } from '../lib/formato';
import { DIAS_SEMANA, horarioVacio, horasPorSemana, rangoValido, resumenHorario } from '../lib/horario';

type Pestana = 'maquinas' | 'materiales' | 'parametros';
const TIPOS: { v: TipoLaser; t: string }[] = [
  { v: 'co2', t: 'CO2' }, { v: 'diodo', t: 'Diodo' }, { v: 'fibra', t: 'Fibra' }, { v: 'mopa', t: 'MOPA' }, { v: 'galvo', t: 'Galvo' },
];

export default function Catalogo() {
  const [params, setParams] = useSearchParams();
  const tab = (['maquinas', 'materiales', 'parametros'].includes(params.get('tab') ?? '') ? params.get('tab') : 'maquinas') as Pestana;
  return (
    <>
      <div className="titulo">
        <h1>Catálogo</h1>
        <div className="seg" role="tablist" aria-label="Sección del catálogo">
          {([['maquinas', 'Máquinas'], ['materiales', 'Materiales'], ['parametros', 'Parámetros']] as const).map(([v, t]) => (
            <button key={v} type="button" role="tab" aria-selected={tab === v} aria-pressed={tab === v} onClick={() => setParams({ tab: v }, { replace: true })}>{t}</button>
          ))}
        </div>
      </div>
      {tab === 'maquinas' && <Maquinas />}
      {tab === 'materiales' && <Materiales />}
      {tab === 'parametros' && <Parametros />}
    </>
  );
}

// ───────────── Máquinas ─────────────

type DatosMaquina = Omit<Maquina, 'id' | 'tenantId'>;
const horarioBase = (): HorarioSemanal => ({ ...horarioVacio(), 1: [['09:00', '18:00']], 2: [['09:00', '18:00']], 3: [['09:00', '18:00']], 4: [['09:00', '18:00']], 5: [['09:00', '18:00']] });
const maquinaNueva = (): DatosMaquina => ({
  nombre: '', marca: '', modelo: '', tipo: 'co2', potenciaW: 60, camaAnchoMm: 600, camaAltoMm: 400, tarifaMinutoCentavos: 500,
  activa: true, esPlaceholder: false, horario: horarioBase(),
});

function Maquinas() {
  const q = useMaquinas();
  const [editar, setEditar] = useState<{ id?: string; datos: DatosMaquina } | null>(null);
  if (q.isLoading) return <Cargando />;
  if (q.isError) return <ErrorCaja error={q.error} reintentar={() => q.refetch()} />;
  return (
    <>
      <div className="fila" style={{ justifyContent: 'flex-end', marginBottom: 10 }}>
        <button type="button" className="btn btn--primario" onClick={() => setEditar({ datos: maquinaNueva() })}>+ Nueva máquina</button>
      </div>
      <div className="tabla-caja">
        <table>
          <thead><tr>
            <th>Máquina</th><th className="oc2">Tipo</th><th className="oc">Cama</th><th className="num">Tarifa/min</th><th className="oc">Horario</th><th>Estado</th><th />
          </tr></thead>
          <tbody>
            {(q.data ?? []).map((m) => (
              <tr key={m.id} className="clic" onClick={() => setEditar({ id: m.id, datos: structuredClone(m) })}>
                <td className="celda-2"><b>{m.nombre}</b><small>{[m.marca, m.modelo].filter(Boolean).join(' · ')}</small></td>
                <td className="oc2">{TIPOS.find((x) => x.v === m.tipo)?.t} · {m.potenciaW} W</td>
                <td className="oc">{medidas(m.camaAnchoMm, m.camaAltoMm)}</td>
                <td className="num">{pesos(m.tarifaMinutoCentavos, true)}</td>
                <td className="oc celda-2"><span>{resumenHorario(m.horario)}</span><small>{numero(horasPorSemana(m.horario))} h/semana</small></td>
                <td>
                  <div className="fila" style={{ gap: 4 }}>
                    <Insignia tono={m.activa ? 'good' : 'neutral'}>{m.activa ? 'Activa' : 'Inactiva'}</Insignia>
                    {m.esPlaceholder && <Insignia tono="warn" title="Datos de ejemplo: confirmar con la máquina real">Placeholder</Insignia>}
                  </div>
                </td>
                <td className="derecha"><button type="button" className="btn btn--sm">Editar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editar && <DialogoMaquina inicial={editar} onCerrar={() => setEditar(null)} />}
    </>
  );
}

function DialogoMaquina({ inicial, onCerrar }: { inicial: { id?: string; datos: DatosMaquina }; onCerrar: () => void }) {
  const [d, setD] = useState<DatosMaquina>(inicial.datos);
  const [borrar, setBorrar] = useState(false);
  const guardar = useGuardarMaquina();
  const eliminar = useEliminarMaquina();
  const avisar = useAvisar();
  const set = (c: Partial<DatosMaquina>) => setD((x) => ({ ...x, ...c }));
  const horarioOk = Object.values(d.horario).every((rs) => rs.every(rangoValido));
  const valido = d.nombre.trim() && d.camaAnchoMm > 0 && d.camaAltoMm > 0 && d.tarifaMinutoCentavos >= 0 && horarioOk;

  const enviar = () => {
    // Quitamos id/tenantId si venían del objeto original.
    const { id: _i, tenantId: _t, ...datos } = d as DatosMaquina & { id?: string; tenantId?: string };
    guardar.mutate({ id: inicial.id, datos }, { onSuccess: () => { avisar('Máquina guardada', 'ok'); onCerrar(); }, onError: (e) => avisar(mensajeError(e), 'bad') });
  };

  return (
    <Dialogo abierto onCerrar={onCerrar} ancho titulo={inicial.id ? `Editar ${inicial.datos.nombre}` : 'Nueva máquina'}
      pie={<>
        {inicial.id && <button type="button" className="btn btn--peligro" style={{ marginRight: 'auto' }} onClick={() => setBorrar(true)}>Eliminar</button>}
        <button type="button" className="btn" onClick={onCerrar}>Cancelar</button>
        <button type="button" className="btn btn--marca" disabled={!valido || guardar.isPending} onClick={enviar}>{guardar.isPending ? 'Guardando…' : 'Guardar'}</button>
      </>}>
      <div className="campos" style={{ marginTop: 10 }}>
        <Campo etiqueta="Nombre" htmlFor="mq-nombre"><input id="mq-nombre" value={d.nombre} onChange={(e) => set({ nombre: e.target.value })} placeholder="CO2 1390 150W" /></Campo>
        <Campo etiqueta="Marca" htmlFor="mq-marca"><input id="mq-marca" value={d.marca} onChange={(e) => set({ marca: e.target.value })} /></Campo>
        <Campo etiqueta="Modelo" htmlFor="mq-modelo"><input id="mq-modelo" value={d.modelo} onChange={(e) => set({ modelo: e.target.value })} /></Campo>
        <Campo etiqueta="Tipo" htmlFor="mq-tipo">
          <select id="mq-tipo" value={d.tipo} onChange={(e) => set({ tipo: e.target.value as TipoLaser })}>{TIPOS.map((x) => <option key={x.v} value={x.v}>{x.t}</option>)}</select>
        </Campo>
        <Campo etiqueta="Potencia" htmlFor="mq-pot"><EntradaNumero id="mq-pot" valor={d.potenciaW} min={1} sufijo="W" onCambio={(n) => set({ potenciaW: n })} /></Campo>
        <Campo etiqueta="Cama ancho" htmlFor="mq-ca"><EntradaNumero id="mq-ca" valor={d.camaAnchoMm} min={1} sufijo="mm" onCambio={(n) => set({ camaAnchoMm: n })} /></Campo>
        <Campo etiqueta="Cama alto" htmlFor="mq-cl"><EntradaNumero id="mq-cl" valor={d.camaAltoMm} min={1} sufijo="mm" onCambio={(n) => set({ camaAltoMm: n })} /></Campo>
        <Campo etiqueta="Tarifa por minuto de láser" htmlFor="mq-tar">
          <EntradaPesos id="mq-tar" valor={d.tarifaMinutoCentavos} onCambio={(c) => set({ tarifaMinutoCentavos: c ?? 0 })} />
        </Campo>
        <div className="campo ancho fila" style={{ gap: 18 }}>
          <label className="check"><input type="checkbox" checked={d.activa} onChange={(e) => set({ activa: e.target.checked })} /> Activa (se cotiza y se programa)</label>
          <label className="check"><input type="checkbox" checked={d.esPlaceholder} onChange={(e) => set({ esPlaceholder: e.target.checked })} /> Datos de ejemplo (placeholder)</label>
        </div>
      </div>
      <div className="h3" style={{ marginTop: 16 }}>Horario semanal (hora de CDMX) · {numero(horasPorSemana(d.horario))} h/semana</div>
      <EditorHorario valor={d.horario} onCambio={(horario) => set({ horario })} />

      <Confirmar abierto={borrar} titulo="¿Eliminar máquina?" tono="peligro" textoConfirmar="Eliminar" ocupado={eliminar.isPending}
        onCancelar={() => setBorrar(false)}
        onConfirmar={() => eliminar.mutate(inicial.id!, { onSuccess: () => { avisar('Máquina eliminada'); onCerrar(); }, onError: (e) => { setBorrar(false); avisar(mensajeError(e), 'bad'); } })}>
        <p style={{ marginTop: 6 }}>Se borran también sus parámetros. Si tiene trabajos, mejor desactívala.</p>
      </Confirmar>
    </Dialogo>
  );
}

const ORDEN_DIAS = [1, 2, 3, 4, 5, 6, 0] as const;

export function EditorHorario({ valor, onCambio }: { valor: HorarioSemanal; onCambio: (h: HorarioSemanal) => void }) {
  const setDia = (d: keyof HorarioSemanal, rangos: [string, string][]) => onCambio({ ...valor, [d]: rangos });
  return (
    <div>
      <div className="fila" style={{ gap: 6, marginBottom: 6 }}>
        <button type="button" className="btn btn--sm" onClick={() => onCambio({ ...valor, 2: structuredClone(valor[1]), 3: structuredClone(valor[1]), 4: structuredClone(valor[1]), 5: structuredClone(valor[1]) })}>
          Copiar lunes a martes–viernes
        </button>
        <button type="button" className="btn btn--sm btn--fantasma" onClick={() => onCambio(horarioVacio())}>Vaciar</button>
      </div>
      {ORDEN_DIAS.map((d) => (
        <div key={d} className="horario-dia">
          <b style={{ fontSize: '.88rem', paddingTop: 4 }}>{DIAS_SEMANA[d]}</b>
          <div className="horario-rangos">
            {valor[d].length === 0 && <span className="meta">cerrado</span>}
            {valor[d].map((r, i) => (
              <span key={i} className="horario-rango" style={rangoValido(r) ? undefined : { borderColor: 'var(--bad)' }}>
                <input type="time" step={900} value={r[0]} aria-label={`${DIAS_SEMANA[d]} turno ${i + 1} desde`}
                  onChange={(e) => setDia(d, valor[d].map((x, k) => (k === i ? [e.target.value, x[1]] : x)))} />
                –
                <input type="time" step={900} value={r[1]} aria-label={`${DIAS_SEMANA[d]} turno ${i + 1} hasta`}
                  onChange={(e) => setDia(d, valor[d].map((x, k) => (k === i ? [x[0], e.target.value] : x)))} />
                <button type="button" className="btn btn--sm btn--fantasma" aria-label="Quitar turno" onClick={() => setDia(d, valor[d].filter((_, k) => k !== i))}>✕</button>
              </span>
            ))}
            <button type="button" className="btn btn--sm btn--fantasma" onClick={() => setDia(d, [...valor[d], valor[d].length ? ['15:00', '19:00'] : ['09:00', '18:00']])}>+ turno</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ───────────── Materiales ─────────────

type DatosMaterial = Omit<Material, 'id' | 'tenantId'>;
const materialNuevo = (): DatosMaterial => ({
  nombre: '', espesorMm: 3, hojaAnchoMm: 900, hojaAltoMm: 600, precioHojaCentavos: 10000, mermaPct: 0.1, colorVista: '#c8a27a', activo: true, esPlaceholder: false,
});

function Materiales() {
  const q = useMateriales();
  const [editar, setEditar] = useState<{ id?: string; datos: DatosMaterial } | null>(null);
  if (q.isLoading) return <Cargando />;
  if (q.isError) return <ErrorCaja error={q.error} reintentar={() => q.refetch()} />;
  return (
    <>
      <div className="fila" style={{ justifyContent: 'flex-end', marginBottom: 10 }}>
        <button type="button" className="btn btn--primario" onClick={() => setEditar({ datos: materialNuevo() })}>+ Nuevo material</button>
      </div>
      <div className="tabla-caja">
        <table>
          <thead><tr><th>Material</th><th className="num">Espesor</th><th className="oc">Hoja</th><th className="num">Precio hoja</th><th className="num oc2">Merma</th><th>Estado</th><th /></tr></thead>
          <tbody>
            {(q.data ?? []).map((m) => (
              <tr key={m.id} className="clic" onClick={() => setEditar({ id: m.id, datos: structuredClone(m) })}>
                <td><span className="color-muestra" style={{ background: m.colorVista }} /><b>{m.nombre}</b></td>
                <td className="num">{numero(m.espesorMm)} mm</td>
                <td className="oc">{medidas(m.hojaAnchoMm, m.hojaAltoMm)}</td>
                <td className="num">{pesos(m.precioHojaCentavos, true)}</td>
                <td className="num oc2">{porcentaje(m.mermaPct)}</td>
                <td><div className="fila" style={{ gap: 4 }}>
                  <Insignia tono={m.activo ? 'good' : 'neutral'}>{m.activo ? 'Activo' : 'Inactivo'}</Insignia>
                  {m.esPlaceholder && <Insignia tono="warn">Placeholder</Insignia>}
                </div></td>
                <td className="derecha"><button type="button" className="btn btn--sm">Editar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editar && <DialogoMaterial inicial={editar} onCerrar={() => setEditar(null)} />}
    </>
  );
}

function DialogoMaterial({ inicial, onCerrar }: { inicial: { id?: string; datos: DatosMaterial }; onCerrar: () => void }) {
  const [d, setD] = useState<DatosMaterial>(inicial.datos);
  const [borrar, setBorrar] = useState(false);
  const guardar = useGuardarMaterial();
  const eliminar = useEliminarMaterial();
  const avisar = useAvisar();
  const set = (c: Partial<DatosMaterial>) => setD((x) => ({ ...x, ...c }));
  const valido = d.nombre.trim() && d.espesorMm > 0 && d.hojaAnchoMm > 0 && d.hojaAltoMm > 0 && d.mermaPct >= 0 && d.mermaPct < 1;
  const enviar = () => {
    const { id: _i, tenantId: _t, ...datos } = d as DatosMaterial & { id?: string; tenantId?: string };
    guardar.mutate({ id: inicial.id, datos }, { onSuccess: () => { avisar('Material guardado', 'ok'); onCerrar(); }, onError: (e) => avisar(mensajeError(e), 'bad') });
  };
  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={inicial.id ? `Editar ${inicial.datos.nombre} ${inicial.datos.espesorMm} mm` : 'Nuevo material'}
      pie={<>
        {inicial.id && <button type="button" className="btn btn--peligro" style={{ marginRight: 'auto' }} onClick={() => setBorrar(true)}>Eliminar</button>}
        <button type="button" className="btn" onClick={onCerrar}>Cancelar</button>
        <button type="button" className="btn btn--marca" disabled={!valido || guardar.isPending} onClick={enviar}>{guardar.isPending ? 'Guardando…' : 'Guardar'}</button>
      </>}>
      <div className="campos" style={{ marginTop: 10 }}>
        <Campo etiqueta="Nombre" htmlFor="mt-nombre"><input id="mt-nombre" value={d.nombre} onChange={(e) => set({ nombre: e.target.value })} placeholder="MDF" /></Campo>
        <Campo etiqueta="Espesor" htmlFor="mt-esp"><EntradaNumero id="mt-esp" valor={d.espesorMm} paso={0.1} min={0} sufijo="mm" onCambio={(n) => set({ espesorMm: n })} /></Campo>
        <Campo etiqueta="Hoja ancho" htmlFor="mt-ha"><EntradaNumero id="mt-ha" valor={d.hojaAnchoMm} min={1} sufijo="mm" onCambio={(n) => set({ hojaAnchoMm: n })} /></Campo>
        <Campo etiqueta="Hoja alto" htmlFor="mt-hl"><EntradaNumero id="mt-hl" valor={d.hojaAltoMm} min={1} sufijo="mm" onCambio={(n) => set({ hojaAltoMm: n })} /></Campo>
        <Campo etiqueta="Precio por hoja" htmlFor="mt-precio"><EntradaPesos id="mt-precio" valor={d.precioHojaCentavos} onCambio={(c) => set({ precioHojaCentavos: c ?? 0 })} /></Campo>
        <Campo etiqueta="Merma" htmlFor="mt-merma" ayuda="Desperdicio extra sobre el acomodo.">
          <EntradaNumero id="mt-merma" valor={Math.round(d.mermaPct * 1000) / 10} min={0} max={99} paso={1} sufijo="%" onCambio={(n) => set({ mermaPct: n / 100 })} />
        </Campo>
        <Campo etiqueta="Color en la vista" htmlFor="mt-color">
          <input id="mt-color" type="color" value={d.colorVista} onChange={(e) => set({ colorVista: e.target.value })} style={{ padding: 2, height: 38 }} />
        </Campo>
        <div className="campo ancho fila" style={{ gap: 18 }}>
          <label className="check"><input type="checkbox" checked={d.activo} onChange={(e) => set({ activo: e.target.checked })} /> Activo (aparece en la página)</label>
          <label className="check"><input type="checkbox" checked={d.esPlaceholder} onChange={(e) => set({ esPlaceholder: e.target.checked })} /> Precio por confirmar (placeholder)</label>
        </div>
      </div>
      <Confirmar abierto={borrar} titulo="¿Eliminar material?" tono="peligro" textoConfirmar="Eliminar" ocupado={eliminar.isPending}
        onCancelar={() => setBorrar(false)}
        onConfirmar={() => eliminar.mutate(inicial.id!, { onSuccess: () => { avisar('Material eliminado'); onCerrar(); }, onError: (e) => { setBorrar(false); avisar(mensajeError(e), 'bad'); } })}>
        <p style={{ marginTop: 6 }}>Se borran también sus parámetros. Si hay trabajos con este material, mejor desactívalo.</p>
      </Confirmar>
    </Dialogo>
  );
}

// ───────────── Parámetros (matriz) ─────────────

function Parametros() {
  const maquinas = useMaquinas();
  const materiales = useMateriales();
  const parametros = useParametros();
  const [editar, setEditar] = useState<{ maquina: Maquina; material: Material; existente?: Parametro } | null>(null);
  if (maquinas.isLoading || materiales.isLoading || parametros.isLoading) return <Cargando />;
  const err = maquinas.error ?? materiales.error ?? parametros.error;
  if (err) return <ErrorCaja error={err} />;
  const buscar = (maq: string, mat: string) => parametros.data!.find((p) => p.maquinaId === maq && p.materialId === mat);

  return (
    <>
      <p className="muted" style={{ marginBottom: 10, fontSize: '.9rem' }}>
        Cada celda es una combinación máquina × material. Sin parámetros, esa máquina no puede cotizar ese material.
      </p>
      <div className="tabla-caja">
        <table className="matriz">
          <thead>
            <tr>
              <th>Máquina \ Material</th>
              {materiales.data!.map((m) => <th key={m.id} style={{ whiteSpace: 'normal', minWidth: 110 }}>{m.nombre}<br />{numero(m.espesorMm)} mm</th>)}
            </tr>
          </thead>
          <tbody>
            {maquinas.data!.map((maq) => (
              <tr key={maq.id}>
                <td className="celda-2"><b>{maq.nombre}</b><small>{maq.activa ? '' : 'inactiva'}</small></td>
                {materiales.data!.map((mat) => {
                  const p = buscar(maq.id, mat.id);
                  return (
                    <td key={mat.id}>
                      <button type="button" className={`celda-param${p ? ' celda-param--si' : ''}`} onClick={() => setEditar({ maquina: maq, material: mat, existente: p })}
                        aria-label={`${p ? 'Editar' : 'Agregar'} parámetros ${maq.nombre} con ${mat.nombre} ${mat.espesorMm} mm`}>
                        {p ? <>corte {numero(p.velCorteMmS)} mm/s ×{p.pasadasCorte}<br />grab. {numero(p.velGrabadoMmS)} · perf. {numero(p.perforacionS)} s</> : '+ agregar'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editar && <DialogoParametro {...editar} onCerrar={() => setEditar(null)} />}
    </>
  );
}

function DialogoParametro({ maquina, material, existente, onCerrar }: { maquina: Maquina; material: Material; existente?: Parametro; onCerrar: () => void }) {
  const [p, setP] = useState<Parametro>(existente ?? {
    maquinaId: maquina.id, materialId: material.id, velCorteMmS: 15, pasadasCorte: 1, velMarcadoMmS: 150, velGrabadoMmS: 300, intervaloLineaMm: 0.1, perforacionS: 0.4,
  });
  const guardar = useGuardarParametro();
  const eliminar = useEliminarParametro();
  const avisar = useAvisar();
  const set = (c: Partial<Parametro>) => setP((x) => ({ ...x, ...c }));
  const valido = p.velCorteMmS > 0 && p.pasadasCorte >= 1 && p.velMarcadoMmS > 0 && p.velGrabadoMmS > 0 && p.intervaloLineaMm > 0 && p.perforacionS >= 0;
  const segMetro = (1000 / p.velCorteMmS) * p.pasadasCorte;
  const segCm2 = 100 / (p.velGrabadoMmS * p.intervaloLineaMm);

  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={`${maquina.nombre} × ${material.nombre} ${material.espesorMm} mm`}
      pie={<>
        {existente && (
          <button type="button" className="btn btn--peligro" style={{ marginRight: 'auto' }} disabled={eliminar.isPending}
            onClick={() => eliminar.mutate(p, { onSuccess: () => { avisar('Parámetros eliminados'); onCerrar(); }, onError: (e) => avisar(mensajeError(e), 'bad') })}>
            Quitar combinación
          </button>
        )}
        <button type="button" className="btn" onClick={onCerrar}>Cancelar</button>
        <button type="button" className="btn btn--marca" disabled={!valido || guardar.isPending}
          onClick={() => guardar.mutate(p, { onSuccess: () => { avisar('Parámetros guardados', 'ok'); onCerrar(); }, onError: (e) => avisar(mensajeError(e), 'bad') })}>
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </>}>
      <div className="campos" style={{ marginTop: 10 }}>
        <Campo etiqueta="Velocidad de corte" htmlFor="pa-vc"><EntradaNumero id="pa-vc" valor={p.velCorteMmS} paso={0.5} min={0} sufijo="mm/s" onCambio={(n) => set({ velCorteMmS: n })} /></Campo>
        <Campo etiqueta="Pasadas de corte" htmlFor="pa-pc"><EntradaNumero id="pa-pc" valor={p.pasadasCorte} min={1} onCambio={(n) => set({ pasadasCorte: Math.max(1, Math.round(n)) })} /></Campo>
        <Campo etiqueta="Perforación" htmlFor="pa-pf" ayuda="Por contorno"><EntradaNumero id="pa-pf" valor={p.perforacionS} paso={0.05} min={0} sufijo="s" onCambio={(n) => set({ perforacionS: n })} /></Campo>
        <Campo etiqueta="Velocidad de marcado" htmlFor="pa-vm"><EntradaNumero id="pa-vm" valor={p.velMarcadoMmS} min={0} sufijo="mm/s" onCambio={(n) => set({ velMarcadoMmS: n })} /></Campo>
        <Campo etiqueta="Velocidad de grabado" htmlFor="pa-vg"><EntradaNumero id="pa-vg" valor={p.velGrabadoMmS} min={0} sufijo="mm/s" onCambio={(n) => set({ velGrabadoMmS: n })} /></Campo>
        <Campo etiqueta="Intervalo de línea" htmlFor="pa-il"><EntradaNumero id="pa-il" valor={p.intervaloLineaMm} paso={0.01} min={0} sufijo="mm" onCambio={(n) => set({ intervaloLineaMm: n })} /></Campo>
      </div>
      {valido && <p className="meta" style={{ marginTop: 12 }}>≈ {numero(segMetro)} s por metro de corte · ≈ {numero(segCm2)} s por cm² de grabado</p>}
    </Dialogo>
  );
}
